'use client';
import { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { useRouter } from 'next/navigation';
import {
    addToLocalCart,
    isProductInCart,
    markProductInCart,
    isInServerCart,
    addServerCartId,
    initServerCart,
    dispatchCartUpdate,
    CART_EVENT,
} from '@/lib/cart';
import { clarityEvent } from '@/lib/clarity';

const getAuthUser = () => {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(/(?:^|; )authUser=([^;]*)/);
    if (!match) return null;
    try { return JSON.parse(decodeURIComponent(match[1])); } catch { return null; }
};

export const OutOfStockInfo = () => (
    <div className="w-full flex flex-column align-items-center gap-1">
        <Button
            label= { `We\'re running out of stock   `}
            icon="pi pi-ban"
            disabled
            className="w-full border-0 font-medium text-white"
            style={{ background: '#9ca3af', cursor: 'not-allowed', opacity: 1 }}
            size="small"
        />
       
    </div>
);
export const AddToCart = ({ product, height }) => {
    const router = useRouter();
    const [loading, setLoading] = useState(false);
    const [inCart,  setInCart]  = useState(false);

    useEffect(() => {
        const authUser = getAuthUser();

        if (authUser) {
            // Trigger a single background fetch of server cart IDs (no-op if already done)
            initServerCart();
            setInCart(isInServerCart(product?._id));
        } else {
            setInCart(isProductInCart(product?._id));
        }

        const update = () => {
            const user = getAuthUser();
            setInCart(user ? isInServerCart(product?._id) : isProductInCart(product?._id));
        };
        window.addEventListener(CART_EVENT, update);
        return () => window.removeEventListener(CART_EVENT, update);
    }, [product?._id]);

    const handleAddToCart = async () => {
        clarityEvent('AddToCart');
        window.dispatchEvent(new CustomEvent('ennix:cart-shake'));
        setLoading(true);
        const authUser = getAuthUser();

        if (authUser) {
            try {
                const res = await fetch('/api/user/add-to-cart', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ productId: product?._id, quantity: 1 }),
                });
                const json = await res.json();
                if (res.ok && json?.success !== false) {
                    addServerCartId(product?._id);
                    dispatchCartUpdate();
                    setInCart(true);
                }
            } catch {}
        } else {
            addToLocalCart({
                productId: product?._id,
                slug: product?.slug,
                title: product?.title,
                price: product?.price,
                mrp: product?.mrp,
                coverImage: product?.coverImage,
                quantity: 1,
            });
            markProductInCart(product?._id);
            setInCart(true);
        }
        setLoading(false);
    };

    if (inCart) {
        return (
            <Button
                label="Go to Cart"
                icon="pi pi-arrow-right"
                iconPos="right"
                onClick={() => router.push('/cart')}
                className="w-full bg-green-500 border-0 font-medium text-white hover:bg-green-600"
                size="small"
                style={height ? { height } : undefined}
            />
        );
    }

    return (
        <Button
            label="Add to Cart"
            icon="pi pi-shopping-cart"
            loading={loading}
            onClick={handleAddToCart}
            className="w-full bg-teal-500 font-medium hover:bg-red-600 text-white border-0"
            size="small"
            style={height ? { height } : undefined}
        />
    );
};

export const BuyNowButton = ({ product, height }) => {
    const router = useRouter();
    const [loading, setLoading] = useState(false);

    const handleBuyNow = async () => {
        clarityEvent('BuyNow');
        setLoading(true);
        const authUser = getAuthUser();

        try {
            if (authUser) {
                // Check if already in server cart — no need to add again
                initServerCart();
                if (!isInServerCart(product?._id)) {
                    const res = await fetch('/api/user/add-to-cart', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ productId: product?._id, quantity: 1 }),
                    });
                    const json = await res.json();
                    if (res.ok && json?.success !== false) {
                        addServerCartId(product?._id);
                        dispatchCartUpdate();
                    }
                }
            } else {
                // Add to local cart only if not already there
                if (!isProductInCart(product?._id)) {
                    addToLocalCart({
                        productId: product?._id,
                        slug: product?.slug,
                        title: product?.title,
                        price: product?.price,
                        mrp: product?.mrp,
                        coverImage: product?.coverImage,
                        quantity: 1,
                    });
                    markProductInCart(product?._id);
                    dispatchCartUpdate();
                }
            }
        } catch { /* silently proceed to cart */ }

        setLoading(false);
        router.push('/cart');
    };

    return (
        <Button
            label="Buy Now"
            icon="pi pi-bolt"
            loading={loading}
            onClick={handleBuyNow}
            className="w-full font-medium bg-primary border-0"
            size="small"
        />
    );
};

export const BuyTemplate = {
    AddToCart,
    BuyNowButton,
    OutOfStockInfo
};
